// ========================================
// UTILIDAD DE SUBIDA DIRECTA A S3
// Combina compresión client-side + presigned URLs
// Elimina Heroku del flujo de archivos completamente
// ========================================

/**
 * Sube archivo directamente a S3 usando presigned URLs
 * @param {File} file - Archivo a subir (ya comprimido)
 * @param {Object} params - Parámetros del survey y question
 * @returns {Promise<Object>} Resultado de la subida
 */
export async function uploadFileDirectlyToS3(file, params) {
    const { surveyId, questionId } = params;
    
    try {
        console.log('🚀 Iniciando subida directa a S3...');
        console.log(`📁 Archivo: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);

        // PASO 1: Solicitar URL firmada al backend
        console.log('📝 Solicitando URL firmada...');
        const urlResponse = await fetch('/formatos/generate_upload_url', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
            },
            body: JSON.stringify({
                survey_id: surveyId,
                question_id: questionId,
                file_name: file.name,
                content_type: file.type,
                file_size: file.size
            })
        });

        if (!urlResponse.ok) {
            const errorData = await urlResponse.json();
            throw new Error(errorData.error || 'Error obteniendo URL firmada');
        }

        const { upload_url, file_key, survey_answer_id, expires_in } = await urlResponse.json();
        console.log('✅ URL firmada obtenida');

        // PASO 2: Subir archivo directamente a S3
        console.log('⬆️ Subiendo archivo a S3...');
        const uploadResponse = await fetch(upload_url, {
            method: 'PUT',
            body: file,
            headers: {
                'Content-Type': file.type,
                'Content-Length': file.size.toString()
            }
        });

        if (!uploadResponse.ok) {
            throw new Error(`Error subiendo a S3: ${uploadResponse.status} ${uploadResponse.statusText}`);
        }

        console.log('✅ Archivo subido a S3 exitosamente');

        // PASO 3: Confirmar subida en el backend
        console.log('✅ Confirmando subida en backend...');
        const confirmResponse = await fetch('/formatos/confirm_file_upload', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
            },
            body: JSON.stringify({
                survey_answer_id: survey_answer_id,
                file_key: file_key
            })
        });

        if (!confirmResponse.ok) {
            const errorData = await confirmResponse.json().catch(() => ({}));
            // Validación IA (Taimes::Agent): el archivo fue rechazado por la validación.
            // No es un error de red — no reintentar ni hacer fallback; devolver el mensaje.
            if (errorData.cancelled) {
                return {
                    success: false,
                    cancelled: true,
                    error: errorData.error || 'El archivo no cumple la validación requerida.',
                    shouldFallback: false
                };
            }
            throw new Error(errorData.error || 'Error confirmando subida');
        }

        const confirmData = await confirmResponse.json();
        console.log('🎉 Subida completada exitosamente');

        return {
            success: true,
            file_url: confirmData.file_url,
            survey_answer_id: confirmData.survey_answer_id,
            file_name: file.name,
            message: 'Archivo subido exitosamente'
        };

    } catch (error) {
        console.error('❌ Error en subida directa S3:', error);
        
        // Retornar error para que el llamador decida el fallback
        return {
            success: false,
            error: error.message,
            shouldFallback: true
        };
    }
}

/**
 * Upload con progreso y retry automático
 * @param {File} file - Archivo a subir
 * @param {Object} params - Parámetros
 * @param {Function} onProgress - Callback de progreso
 * @returns {Promise<Object>} Resultado
 */
export async function uploadFileWithProgress(file, params, onProgress = null) {
    const maxRetries = 3;
    let attempt = 0;

    while (attempt < maxRetries) {
        try {
            if (onProgress) {
                onProgress({
                    phase: 'preparing',
                    progress: 0,
                    message: "Subiendo archivo..."
                });
            }

            const result = await uploadFileDirectlyToS3(file, params);

            // Rechazo por validación de IA (Taimes::Agent): no reintentar, salir con el mensaje.
            if (result.cancelled) {
                if (onProgress) {
                    onProgress({ phase: 'failed', progress: 0, message: result.error });
                }
                return result;
            }

            if (result.success) {
                if (onProgress) {
                    onProgress({
                        phase: 'completed',
                        progress: 100,
                        message: '¡Archivo subido exitosamente!'
                    });
                }
                return result;
            } else {
                throw new Error(result.error);
            }

        } catch (error) {
            attempt++;
            console.warn(`⚠️ Intento ${attempt} falló:`, error.message);

            if (attempt >= maxRetries) {
                if (onProgress) {
                    onProgress({
                        phase: 'failed',
                        progress: 0,
                        message: 'Error subiendo archivo'
                    });
                }
                
                return {
                    success: false,
                    error: error.message,
                    shouldFallback: true
                };
            }

            // Esperar antes del siguiente intento
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
            
            if (onProgress) {
                onProgress({
                    phase: 'retrying',
                    progress: 0,
                    message: "Subiendo archivo..."
                });
            }
        }
    }
}

/**
 * Valida si el archivo puede subirse directamente a S3
 * @param {File} file - Archivo a validar
 * @returns {Object} Resultado de validación
 */
export function validateFileForS3Upload(file) {
    // Validar tamaño (100MB máximo)
    if (file.size > 104857600) {
        return {
            isValid: false,
            error: "El archivo excede el límite de 100 MB permitido"
        };
    }

    // Validar tipo de archivo (opcional - S3 acepta cualquier tipo)
    const allowedTypes = [
        // Imágenes
        'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
        // Documentos
        'application/pdf', 'application/msword', 
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        // Otros
        'text/plain', 'text/csv'
    ];

    // Por ahora, permitir todos los tipos (Beewo maneja validación en backend)
    return {
        isValid: true,
        warning: allowedTypes.includes(file.type) ? null : 
                'Tipo de archivo no común, pero será procesado'
    };
}

/**
 * Función principal que combina compresión + subida directa
 * Diseñada para reemplazar el flujo actual manteniendo compatibilidad
 * @param {File} originalFile - Archivo original del usuario
 * @param {Object} params - Parámetros del survey
 * @param {Function} onProgress - Callback opcional de progreso
 * @returns {Promise<Object>} Resultado completo
 */
export async function compressAndUploadToS3(originalFile, params, onProgress = null) {
    try {
        // Importar utilidad de compresión
        const { compressFileForBeewo } = await import('./imageCompression.js');

        // PASO 1: Comprimir archivo si es imagen
        if (onProgress) {
            onProgress({
                phase: 'compressing',
                progress: 25,
                message: '🗜️ Optimizando archivo...'
            });
        }

        console.log('🗜️ Comprimiendo archivo...');
        const compressedFile = await compressFileForBeewo(originalFile);
        
        const originalSizeMB = (originalFile.size / 1024 / 1024).toFixed(2);
        const compressedSizeMB = (compressedFile.size / 1024 / 1024).toFixed(2);
        const reduction = originalFile.size !== compressedFile.size ? 
            (((originalFile.size - compressedFile.size) / originalFile.size) * 100).toFixed(1) : 0;

        console.log(`📊 Compresión: ${originalSizeMB}MB → ${compressedSizeMB}MB (${reduction}% reducción)`);

        // Validar tamaño DESPUÉS de comprimir
        const { validateFileForBeewo } = await import('./imageCompression.js');
        const sizeValidation = validateFileForBeewo(compressedFile, { checkSizeLimit: true, checkFileType: false });
        if (!sizeValidation.isValid) {
            return {
                success: false,
                error: `${sizeValidation.message} (Archivo original: ${originalSizeMB}MB, después de comprimir: ${compressedSizeMB}MB)`,
                shouldFallback: false
            };
        }

        // PASO 2: Subir directamente a S3
        if (onProgress) {
            onProgress({
                phase: 'uploading',
                progress: 50,
                message: '☁️ Subiendo a la nube...'
            });
        }

        const uploadResult = await uploadFileWithProgress(
            compressedFile, 
            params,
            (uploadProgress) => {
                if (onProgress) {
                    onProgress({
                        phase: 'uploading',
                        progress: 50 + (uploadProgress.progress * 0.5), // 50-100%
                        message: uploadProgress.message
                    });
                }
            }
        );

        return {
            ...uploadResult,
            compressionData: {
                originalSize: originalFile.size,
                compressedSize: compressedFile.size,
                reduction: reduction + '%'
            }
        };

    } catch (error) {
        console.error('❌ Error en proceso completo:', error);
        return {
            success: false,
            error: error.message,
            shouldFallback: true
        };
    }
}

// Exportar funciones para uso directo
export default {
    uploadFileDirectlyToS3,
    uploadFileWithProgress,
    validateFileForS3Upload,
    compressAndUploadToS3
};